<template>
  <li>
    <ul
      v-for="comment in updatedComments"
      :key="comment.id"
      class="reply-comments pl-0"
      :id="`comment-${comment.id}`"
    >
      <CommentComponent
        :comment="comment"
        :user="user"
        :hashtagsEnabled="hashtagsEnabled"
        :hashtags="hashtags"
        @addComment="addComment"
        @editComment="editComment"
        @onHashtag="onHashtag"
      />
    </ul>
  </li>
</template>

<script>
import moment from 'moment';
import { generateHTML } from '@tiptap/html';
import Text from '@tiptap/extension-text';
import Paragraph from '@tiptap/extension-paragraph';
import Document from '@tiptap/extension-document';
import CommunityMention from '@/web/components/community/community-mention';
import Image from '@tiptap/extension-image';
import HardBreak from '@tiptap/extension-hard-break';
import Heading from '@tiptap/extension-heading';
import Code from '@tiptap/extension-code';
import CodeBlock from '@tiptap/extension-code-block';
import Blockquote from '@tiptap/extension-blockquote';
import BulletList from '@tiptap/extension-bullet-list';
import OrderedList from '@tiptap/extension-ordered-list';
import ListItem from '@tiptap/extension-list-item';
import Link from '@tiptap/extension-link';
import Italic from '@tiptap/extension-italic';
import Bold from '@tiptap/extension-bold';
import { mapGetters } from 'vuex';
import { sanitize } from '@/core/utils/utils';

export default {
  name: 'CommunityCommentNode',
  components: {
    CommentComponent: () => import('./CommentComponent'),
  },
  props: {
    comments: Array,
    user: Object,
    hashtagsEnabled: {
      type: Boolean,
    },
    hashtags: {
      type: Array,
    },
  },
  computed: {
    ...mapGetters({
      studioCommunityProfileId: 'user/studioCommunityProfileId',
      interactAsStudio: 'community/interactAsStudio',
      studio: 'studio/currentStudio',
    }),
  },
  data() {
    return {
      comment: {},
      updatedComments: [],
      inEditMode: false,
      activeCommentId: null,
    };
  },
  methods: {
    jsonToHtmlContent(jsonContent) {
      if (typeof jsonContent === 'string') {
        jsonContent = JSON.parse(jsonContent);
      }
      try {
        return sanitize(
          generateHTML(jsonContent, [
            Document,
            Paragraph,
            Text,
            CommunityMention.CommunityMention,
            Image,
            HardBreak,
            Heading,
            Code,
            CodeBlock,
            BulletList,
            OrderedList,
            ListItem,
            Link,
            Italic,
            Bold,
            Blockquote,
          ])
        );
      } catch (e) {
        console.log(e);
        return '<p class="c-coral">ERROR parsing comment!</p>';
      }
    },
    addComment(data) {
      this.$emit('addComment', data);
    },
    parseComments() {
      if (!this.comments?.length > 0) return [];
      const comments = this.comments.map(comment => {
        return {
          ...comment,
          parsedContent: this.jsonToHtmlContent(comment.content),
          isScheduled:
            comment.release_on && moment(comment.release_on).isAfter(moment()),
        };
      });
      this.updatedComments = Object.assign([], comments);
    },
    editComment(comment) {
      this.$emit('editComment', comment);
    },
    onCommentEdit(comment) {
      this.$emit('editComment', comment);
    },
    onHashtag(hashtag) {
      this.$emit('onHashtag', hashtag);
    },
  },
  mounted() {
    this.parseComments();
  },
  watch: {
    comments: {
      handler(comments) {
        this.parseComments();
      },
      immediate: true,
      deep: true,
    },
  },
};
</script>

<style lang="scss" scoped>
@import '@/web/styles/components-variables.scss';
.comment {
  &__block-wrapper {
    width: 100%;
    overflow: hidden;
    word-break: break-word;

    &--small {
      width: auto;
    }

    &.scheduled {
      opacity: 0.9;
    }
  }

  &__header {
    .popup-menu {
      position: absolute;
      right: 1rem;
      top: 1rem;
    }
  }

  &__block {
    padding: 1rem;
    border: 1px solid $light-gray;
    border-radius: $border-radius;
    background-color: $white;
    position: relative;

    &--opened {
      border-radius: $border-radius $border-radius 0 0;
      border-bottom-color: transparent;
    }

    &--small {
      padding: 9px 16px 6px 16px;
      border-radius: $border-radius;
      border: none;
    }
  }

  &__label {
    font-size: 12px;
    font-weight: 400;
    color: $dark-gray;

    &--liked {
      color: var(--brand-color, $coral);
    }

    &--medium-grey {
      color: $medium-gray;
    }
  }

  &__like-reply-block {
  }

  &__like-block {
    position: absolute;
    right: 5px;
    bottom: -10px;
    background-color: $white;
    border-radius: $border-radius;
    box-shadow: 2px 3px 9px -7px $black;
    &:hover .comment__label {
      text-decoration: underline;
    }
  }

  &__like-container {
    background-color: $white;
    border-radius: $border-radius;
    text-align: center;
    cursor: pointer;
  }

  &__like-icon {
    color: var(--brand-color, $coral);

    &--comments {
      flex: 1;
      font-size: 11px;
      color: var(--brand-color, $coral);
    }
  }

  &__reply-block {
    color: $white;
    background-color: $white;

    &.hidden {
      display: none !important;
    }

    &--nested {
      border-top: none !important;
      border-radius: 0 0 $border-radius $border-radius !important;
    }
  }

  &__icon {
    cursor: pointer;
    height: 48px;
    width: 48px;

    &--small {
      margin-left: 0;
      height: 40px;
      width: 40px;
    }
  }

  &__like-number--active:hover {
    text-decoration: underline;
  }

  &__children {
    background: $lightest-gray;
    margin: 0 !important;
    padding-top: 12px;
  }

  &__children:last-of-type {
    border-radius: 0 0 $border-radius $border-radius;
  }

  &__circle {
    color: $medium-gray;
    font-size: 4px;
  }

  p {
    margin: 0;
  }
}

::v-deep {
  .mention {
    color: var(--brand-color, $coral);
    padding-right: 5px;
  }
}

.reply-comments {
  border-radius: $border-radius;
}

.comment-content {
  color: $charcoal;

  ::v-deep {
    p {
      margin-bottom: 0.5rem;
    }
    img {
      border-radius: 4px;
      max-width: 50vw;
      width: 300px;
      margin-bottom: 0.5rem;
      margin-right: 0.5rem;
    }
    a {
      color: var(--brand-color, $coral);
    }
  }
}

.author-name {
  font-weight: 600;
}

.author-role {
  color: $dark-gray;
  &.student {
    display: none !important;
  }
}

.charcoal-on-hover {
  &:hover {
    color: $charcoal;
    transition: all 0.2s ease-in-out;
  }
}

.underline-on-hover {
  &:hover {
    text-decoration: underline;
    transition: all 0.2s ease-in-out;
  }
}

hr {
  border-top-color: $lightest-gray;
}

ul {
  padding-left: 0;
  margin-left: 0;
  margin-bottom: 10px;
}

ul li {
  list-style-type: none;
}

.hashtag {
  border: 1px solid var(--brand-color, $coral);
  border-radius: $border-radius;
  background-color: transparent;
  color: var(--brand-color, $coral);
  padding: 4px 8px;
  outline: none;

  &:hover {
    background-color: #f7f7f7;
  }
}

.calendar-icon {
  width: 24px;
  height: 24px;
  border-radius: 50%;
  display: flex;
  justify-content: center;
  align-items: center;
  background: var(--brand-color-with-opacity-03, $coral);
  color: var(--brand-color, $coral);
}
</style>
